import React, { useEffect, useState } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from "@mui/material";
import {
    normalizeEmailMergeVariableName,
    validateEmailMergeVariableNameInput,
} from "../../utils/emailMergeVariables";

/**
 * Prompt for a meaningful merge-parameter name (insert or rename).
 */
const EmailMergeParameterNameDialog = ({
    open,
    mode = "insert",
    initialName = "",
    usedNames = [],
    excludeName = null,
    onClose,
    onConfirm,
}) => {
    const [value, setValue] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open) return;
        setValue(mode === "rename" ? initialName : "");
        setError("");
    }, [open, mode, initialName]);

    const handleConfirm = () => {
        const validationError = validateEmailMergeVariableNameInput(value, usedNames, {
            excludeName: mode === "rename" ? excludeName || initialName : null,
        });
        if (validationError) {
            setError(validationError);
            return;
        }

        const normalized = normalizeEmailMergeVariableName(value);
        if (!normalized) {
            setError("Enter a valid name (letters, numbers, underscore; start with a letter or _).");
            return;
        }
        onConfirm?.(normalized);
    };

    const title = mode === "rename" ? "Rename parameter" : "Parameter name";
    const helper =
        mode === "rename"
            ? "Updates every {{tag}} with this name in the template."
            : "Use a meaningful Excel column name, e.g. name or company.";

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ pb: 1 }}>{title}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {helper}
                </Typography>
                <TextField
                    autoFocus
                    fullWidth
                    label="Parameter name"
                    placeholder="e.g. name, company, city"
                    value={value}
                    onChange={(event) => {
                        setValue(event.target.value);
                        if (error) setError("");
                    }}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            event.preventDefault();
                            handleConfirm();
                        }
                    }}
                    error={Boolean(error)}
                    helperText={error || "Letters, numbers, and underscore. Must start with a letter or _."}
                    inputProps={{ spellCheck: false }}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleConfirm}>
                    {mode === "rename" ? "Rename" : "Insert"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EmailMergeParameterNameDialog;
